// This controller manages context modal interactions and bottom navigation indicator

// image_context_view_caller_controller.js
import ApplicationController from "./application_controller";
import { wordSearchNewTabUrl } from "../lib/word_search_new_tab_url";

export default class ImageContextViewCallerController extends ApplicationController {
  static targets = ["activeStageTabIndicator", "stageTabButton", "stageTabsContainer"];
  static values = { failedToLoadContext: String };

  initialize() {
    // To handle multiple modals on a single page
    this.imageViewModalId = this.element.dataset['modalId'] || 'imageModal';
  }

  connect() {
    // Initialize navigation indicator position on load
    requestAnimationFrame(() => this.initializeActiveStageTabIndicator());
  }

  // Get all visible stage tab buttons (excludes hidden and separator elements)
  getStageTabButtons() {
    if (this.hasStageTabButtonTarget) {
      return this.stageTabButtonTargets.filter(btn => !btn.hidden && btn.getAttribute('aria-hidden') !== 'true');
    }
    // TODO: Verify this for stage-level context functionality and remove this fallback. It is suggested that this should be handled through Stimulus targets.
    // Fallback to aria-role if targets not available
    return Array.from(this.element.querySelectorAll('[role="tab"]'))
      .filter(btn => !btn.hidden && btn.getAttribute('aria-hidden') !== 'true');
  }

  // Get the stage tabs container element
  getStageTabsContainer() {
    if (this.hasStageTabsContainerTarget) {
      return this.stageTabsContainerTarget;
    }
    // Fallback to aria-role if target not available
    return this.element.querySelector('[role="tablist"]');
  }

  // Find the currently active stage tab button
  getActiveStageTabButton() {
    const stageTabButtons = this.getStageTabButtons();
    return stageTabButtons.find(btn => btn.getAttribute('aria-selected') === 'true');
  }

  getPreviousActiveTabIndexFromContainer() {
    const container = this.getStageTabsContainer();
    if (!container) return null;

    const rawValue = container.dataset.previousActiveTabIndex;
    if (rawValue === undefined || rawValue === null || rawValue === '') return null;

    const parsed = parseInt(rawValue, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Initialize active stage tab indicator position based on active button
  initializeActiveStageTabIndicator() {
    const activeButton = this.getActiveStageTabButton();
    
    if (!activeButton || !this.hasActiveStageTabIndicatorTarget) return;
    
    const stageTabButtons = this.getStageTabButtons();
    const activeIndex = stageTabButtons.indexOf(activeButton);
    
    if (activeIndex === -1) return;

    const previousIndex = this.getPreviousActiveTabIndexFromContainer();
    const canAnimateFromPrevious =
      previousIndex !== null &&
      previousIndex >= 0 &&
      previousIndex < stageTabButtons.length &&
      previousIndex !== activeIndex;

    if (!canAnimateFromPrevious) {
      this.updateActiveStageTabIndicatorPosition(activeIndex, stageTabButtons.length);
      return;
    }

    const activeTabIndicator = this.activeStageTabIndicatorTarget;
    const originalTransition = activeTabIndicator.style.transition;

    // Place the indicator at previous active tab immediately, then animate to current active tab.
    activeTabIndicator.style.transition = 'none';
    this.updateActiveStageTabIndicatorPosition(previousIndex, stageTabButtons.length);

    requestAnimationFrame(() => {
      activeTabIndicator.style.transition = originalTransition;
      this.updateActiveStageTabIndicatorPosition(activeIndex, stageTabButtons.length);
    });
  }

  // Handle stage tab button click and update indicator
  updateStageTabIndicator(event) {
    if (!this.hasActiveStageTabIndicatorTarget) return;

    const clickedButton = event.currentTarget;
    const stageTabButtons = this.getStageTabButtons();
    const clickedIndex = stageTabButtons.indexOf(clickedButton);
    
    if (clickedIndex === -1) return;

    const currentActiveButton = this.getActiveStageTabButton();
    const currentActiveIndex = stageTabButtons.indexOf(currentActiveButton);
    if (currentActiveIndex !== -1) {
      clickedButton.dataset.previousActiveTabIndex = currentActiveIndex;
    }
    
    // Update button states and indicator position
    this.updateStageTabActiveState(clickedButton, stageTabButtons);
    this.updateActiveStageTabIndicatorPosition(clickedIndex, stageTabButtons.length);
  }

  // Calculate and set stage tab indicator position via CSS custom properties
  updateActiveStageTabIndicatorPosition(index, totalButtons) {
    if (!this.hasActiveStageTabIndicatorTarget) return;
    
    const container = this.getStageTabsContainer();
    if (!container) return;
    
    // Calculate dimensions for backward compatibility
    const containerWidth = container.offsetWidth;
    const containerStyle = window.getComputedStyle(container);
    const containerPadding =
      (parseFloat(containerStyle.paddingLeft) || 0) +
      (parseFloat(containerStyle.paddingRight) || 0);
    const availableWidth = containerWidth - containerPadding;
    const buttonWidth = availableWidth / totalButtons;
    const position = index * buttonWidth;
    
    // Set inline styles for immediate compatibility
    this.activeStageTabIndicatorTarget.style.width = `${buttonWidth}px`;
    this.activeStageTabIndicatorTarget.style.transform = `translateX(${position}px)`;
    
    // Also set CSS custom properties for future CSS-driven approach
    container.style.setProperty('--active-tab-index', index);
    container.style.setProperty('--total-tabs', totalButtons);
  }

  // Update active/inactive states for stage tab buttons
  updateStageTabActiveState(clickedButton, stageTabButtons) {
    // Deactivate all buttons
    stageTabButtons.forEach(btn => {
      btn.setAttribute('aria-selected', 'false');
      btn.removeAttribute('disabled');
    });
    
    // Activate clicked button
    clickedButton.setAttribute('aria-selected', 'true');
    clickedButton.setAttribute('disabled', '');
  }

  currentLocaleParam() {
    // Only respect an explicit locale param from the URL (set by the language toggle).
    // If none is present, return null and let the server decide based on its own rules
    // (session, browser language, etc.).
    try {
      const urlLocale = new URLSearchParams(window.location.search).get('locale');
      if (urlLocale && ['en', 'tu'].includes(urlLocale)) return urlLocale;
    } catch (e) {
      // ignore
    }

    return null;
  }

  openImageModal() { // Dead Code Added new openImageModalAjaxCall
    this.imageContextController().openImageModal();
  }

  imageContextController(){ // Dead Code Added new openImageModalAjaxCall
    let imageModalElement = document.querySelector(`#${this.imageViewModalId}`);
    let imageContextViewController = this.application.getControllerForElementAndIdentifier(
      imageModalElement, "image-context-view"
    );
    return imageContextViewController;
  }

  // For page_set
  openCropperContextAjaxCall(e) {
    // Get initial_object_id from the button's data attribute (passed from modal)
    const initialObjectId = e.currentTarget.dataset.initialObjectId;
    
    const data = {
      object_id: e.currentTarget.dataset.objectId,
      page_set_id: e.currentTarget.dataset.pageSetId,
      stage: e.currentTarget.dataset.stage,
      object_stage: e.currentTarget.dataset.objectStage,
      context_on_object_id: e.currentTarget.dataset.contextOnItemId,
     }
    
    if (initialObjectId) {
      data.initial_object_id = initialObjectId;
    }

    const previousActiveTabIndex = e.currentTarget.dataset.previousActiveTabIndex;
    if (previousActiveTabIndex !== undefined && previousActiveTabIndex !== '') {
      data.previous_active_tab_index = previousActiveTabIndex;
    }

    // Include locale to ensure server returns content in the user's explicitly selected language preference
    const locale = this.currentLocaleParam();
    if (locale) {
      data.locale = locale;
    }
    
    $.ajax({
      url: `/page_set/context/show_item_context`,
      type: 'GET',
      data: data
    });
  }

  openImageModalAjaxCall(e){
    // Skip if the click originated inside a container marked to not propagate
    const clickedElement = e && e.target ? e.target : null;
    if (!clickedElement) { return; }

    // TODO(after_major_release): Avoid class-name coupling here. Prefer a stable data attribute
    // Prevent event bubbling when programmatically clicking the hidden context button
    // This stops the click from bubbling to parent word box and triggering word search
    if (e.currentTarget?.classList?.contains('js-search-output-result-context-view-btn')) {
      e.stopPropagation();
    }

    // Apply interactive-element skip ONLY for clicks originating inside the spelling-linkage modal
    // const isFromSpellingLinkageModal = !!clickedElement.closest('.js-spelling-linkage-modal');

    const path = e.composedPath ? e.composedPath() : (e.path || []);

    // CONDITION 1: Check if click happened inside spelling-linkage modal
    const isInsideSpellingModal = path.some(el => el?.classList?.contains('js-spelling-linkage-modal'));

    if (isInsideSpellingModal) {
      // Case 1: Clicked inside text field - No Context Zone, skip
      const isInsideTextField = path.some(el => el?.classList?.contains('js-nocontext--modal-on-click'));

      if (isInsideTextField) {
        e.stopPropagation();
        return;
      }

      // Case 2: Interactive elements inside spelling modal skip
      const isInteractive = path.some(el => {
        if (!el || !el.tagName) return false;
        
        const tag = el.tagName.toLowerCase();

        return (
          ['input', 'textarea', 'button', 'select', 'label', 'a'].includes(tag) ||
          (el.getAttribute && (
            el.getAttribute('contenteditable') === 'true' ||
            el.getAttribute('role') === 'switch'
          ))
        );
      });

      if (isInteractive) {
        e.stopPropagation();
        return;
      }      
    }

    let data = {};
    if (e.currentTarget.dataset.contextCallFromPreview) {
      let selectedItem = document.querySelector('.js-preview-tab-content.active').querySelector('.js-preview-active-item-with-link.active');//get the current selected preview item
      const itemId = selectedItem.getAttribute('data-item-id');
      
      data = {
        object_id:   itemId,
        page_set_id: selectedItem.getAttribute('data-page-set-id'),
        context_on_object_id: selectedItem.dataset.contextOnItemId,
        initial_object_id: itemId
      }
    }
    else {
      data = {
        object_id:   e.currentTarget.dataset.itemId,
        page_set_id: e.currentTarget.dataset.pageSetId,
        context_on_object_id: e.currentTarget.dataset.contextOnItemId,
        initial_object_id: e.currentTarget.dataset.initialObjectId,
      }
    }
    data['stage']        = 'typing'
    data['object_stage'] = 'on-region'
    // Include locale to ensure server returns content in the user's explicitly selected language preference
    const locale = this.currentLocaleParam();
    if (locale) {
      data['locale'] = locale;
    }

    const controller = this;
    $.ajax({
      url: '/page_set/context/show_item_context',
      type: 'GET',
      data: data
    }).fail(function(xhr, status, error) {
      console.error('Failed to load Location modal:', error);
      const errorMessage = controller.failedToLoadContextValue || 'Failed to load location. Please try again.'; //ref#2032
      showFlashMessage(errorMessage, 'error', 'float')
    });
  }

  openContextModalFromSearchOutputRowClick(e) {
    const path = e.composedPath ? e.composedPath() : (e.path || []);

    // Combined check for all exclusions
    const isExcluded = path.some(el => {
      if (!el || !el.classList || !el.tagName) return false;
      
      return (
        el.tagName.toLowerCase() === 'button' ||
        // Exclude elements that declare themselves as "row-click excluded".
        // This is intentionally based on a semantic data-attribute (more stable than class names).
        (el.dataset && el.dataset.skipSearchOutputRowClick === 'true') ||
        ['modal', 'modal-dialog', 'modal-content', 'modal-header', 'modal-body', 'modal-footer', 'btn-close']
          .some(className => el.classList.contains(className))
      );
    });
    
    if (isExcluded) {
      return; // Don't process row click if clicking on excluded elements
    }

    const clickedRow = e.currentTarget;
    const clickedElement = e.target;

    // Check if click was on left group (Result) or right group (Entry)
    const isLeftGroupClick = clickedElement.closest('.search-result-left-group');
    const isRightGroupClick = clickedElement.closest('.search-result-right-group');

    // Redhouse (LexiQamus import): open LexiQamus in a new tab; no BuildLQ context popup.
    // Left/Result → matched word URL; Right/Entry → parent Entry URL.
    const lexiqamusRedhouseResultUrl = clickedRow?.dataset?.lexiqamusRedhouseResultUrl;
    const lexiqamusRedhouseEntryUrl = clickedRow?.dataset?.lexiqamusRedhouseEntryUrl;
    if (lexiqamusRedhouseResultUrl || lexiqamusRedhouseEntryUrl) {
      if (isRightGroupClick && lexiqamusRedhouseEntryUrl) {
        window.open(lexiqamusRedhouseEntryUrl, '_blank');
      } else if (lexiqamusRedhouseResultUrl) {
        window.open(lexiqamusRedhouseResultUrl, '_blank');
      }
      return;
    }

    if (isLeftGroupClick) {
      // Left side: Open Result word context (matched_item)
      const resultViewButton = clickedRow?.querySelector('.js-search-output-result-context-view-btn');
      if (resultViewButton) {
        resultViewButton.click();
      }
    } else if (isRightGroupClick) {
      // Right side: Open Primary Spelling context
      const primarySpellingViewButton = clickedRow?.querySelector('.js-search-output-primary-spelling-context-view-btn');
      if (primarySpellingViewButton) {
        primarySpellingViewButton.click();
      }
    }
  }

  // Keyboard activation for focusable result rows (Enter/Space opens result context).
  handleSearchResultRowKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
    const row = e.currentTarget;
    const leftGroup = row.querySelector('.search-result-left-group');
    this.openContextModalFromSearchOutputRowClick({
      currentTarget: row,
      target: leftGroup || row,
      composedPath: () => [leftGroup || row, row],
    });
  }

  // Handle click on search values (Latin/Ottoman text) to open new search
  searchClickedWordInNewTab(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // IMPORTANT:
    // The word box now contains additional interactive UI (misspelled indicator button, popup card,
    // hidden context buttons, SVGs). Using `e.currentTarget.textContent` would pollute the extracted
    // search term (e.g. include "!" or "Original misspelled word"), resulting in wrong searches.
    //
    // To keep behavior stable for this release, we clone the node and strip non-word UI before reading
    // `textContent`.
    //
    // TODO(after_major_release): We should modify/improve the logic of extracting searched term from DOM text.
    // Instead we can render a stable value on the word box (example: `data-search-text="kitap"`) and read it via:
    // `const searchValueText = e.currentTarget.dataset.searchText;`
    // This will be more reliable, faster, and won't break when UI markup changes.
    //
    // Clone the element and remove non-word UI (indicator/popup/buttons) to get clean text
    const element = e.currentTarget.cloneNode(true);
    
    // Remove misspelled UI + any nested buttons/icons that pollute text extraction
    element.querySelectorAll(
      '[data-misspelled-search-result-target="misspelledIndicator"], [data-misspelled-search-result-target="misspelledPopupCard"], .js-search-output-result-context-view-btn, button, svg'
    ).forEach(el => el.remove());
    
    // Back-compat: also remove any aria-hidden children
    element.querySelectorAll('[aria-hidden="true"]').forEach(el => el.remove());
    
    const searchValueText = element.textContent.trim();
    
    if (!searchValueText) {
      // showFlashMessage('Search value not present', 'error', 'float')
      console.log('Search value not present');
      return
    }
    
    const searchUrl = wordSearchNewTabUrl(searchValueText);
    if (!searchUrl) {
      console.log('Search output path not found');
      return;
    }

    window.open(searchUrl, '_blank');
  }
}
